/**
 * Fill Your Day Component
 * 
 * Shows time gaps in a day and suggests activities to fill them
 */

import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { View, ScrollView, Pressable, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import type { Trip } from '@/services/trips';
import type { ItineraryItem } from '@/services/itinerary';
import {
  findTimeGaps,
  suggestAiActivitiesForGap,
  suggestPoisForGap,
  type ActivitySuggestion,
  type TimeGap,
} from '@/services/autoItinerary';
import { createItineraryItem } from '@/services/itinerary';
import { VoteButton } from '@/components/collaboration/VoteButton';
import { useUserStore } from '@/state/userStore';
import { usePremiumGateStore } from '@/state/premiumGateStore';
import { hasPremiumAccess } from '@/services/entitlements';
import {
  trackAiFallbackUsed,
  trackAiRequestCompleted,
  trackAiRequestFailed,
  trackAiRequestStarted,
  trackAiResultAccepted,
} from '@/services/aiFlowAnalytics';

const GAP_SUGGESTION_CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes
const gapSuggestionCache = new Map<
  string,
  { cachedAt: number; poiSuggestions?: ActivitySuggestion[]; aiSuggestions?: ActivitySuggestion[] }
>();

type FillYourDayProps = {
  trip: Trip;
  day: Date;
  existingItems: ItineraryItem[];
  onItemAdded?: (item: ItineraryItem) => void;
};

export function FillYourDay({
  trip,
  day,
  existingItems,
  onItemAdded,
}: FillYourDayProps) {
  const theme = useTheme();
  const profile = useUserStore((state) => state.profile);
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const isPremium = hasPremiumAccess({
    isPremium: profile?.isPremium,
    planTier: profile?.planTier,
    entitlement: profile?.entitlement,
  });
  const openPremiumGate = usePremiumGateStore((state) => state.open);
  const [gapSuggestions, setGapSuggestions] = useState<Record<string, ActivitySuggestion[]>>({});
  const [gaps, setGaps] = useState<TimeGap[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [gapLoading, setGapLoading] = useState<Record<string, boolean>>({});
  const [aiUpgrading, setAiUpgrading] = useState<Record<string, boolean>>({});
  const [aiShownForGap, setAiShownForGap] = useState<Record<string, boolean>>({});
  const [aiAutoRequestedForGap, setAiAutoRequestedForGap] = useState<Record<string, boolean>>({});
  const [aiUpgradePromptForGap, setAiUpgradePromptForGap] = useState<Record<string, boolean>>({});
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [addingItem, setAddingItem] = useState<string | null>(null);
  const refreshNonceRef = useRef(0);
  const autoLimitUpsellShownRef = useRef(false);

  const toLocalDayKey = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const refreshSuggestions = useCallback(async () => {
    refreshNonceRef.current += 1;
    // clear cache for this day to force a refresh
    const prefix = `${trip.id || 'no-trip'}:${toLocalDayKey(day)}:`;
    for (const key of Array.from(gapSuggestionCache.keys())) {
      if (key.startsWith(prefix)) gapSuggestionCache.delete(key);
    }
    // Kick the effect by resetting state quickly; effect will repopulate.
    setGapSuggestions({});
    setGapLoading({});
    setAiUpgrading({});
    setAiShownForGap({});
    setAiAutoRequestedForGap({});
    setAiUpgradePromptForGap({});
    autoLimitUpsellShownRef.current = false;
    setSuggestionsError(null);
    setLoadingSuggestions(true);
  }, [existingItems, day, trip]);

  useEffect(() => {
    let cancelled = false;
    
    const loadSuggestions = async () => {
      try {
        // 1) Compute gaps synchronously (fast) so UI can render immediately.
        const computedGaps = findTimeGaps(existingItems, day, trip);
        if (cancelled) return;
        setGaps(computedGaps);
        setGapSuggestions({});
        setAiShownForGap({});
        setAiAutoRequestedForGap({});
        autoLimitUpsellShownRef.current = false;
        setSuggestionsError(null);

        // 2) Start loading per-gap suggestions (with a small concurrency limit).
        const initialLoading: Record<string, boolean> = {};
        computedGaps.forEach((g) => {
          const k = `${formatHm(g.start)}-${formatHm(g.end)}`;
          initialLoading[k] = true;
        });
        setGapLoading(initialLoading);
        setLoadingSuggestions(false);

        // Sort gaps longest-first so “big blocks” get suggestions first.
        const gapsOrdered = [...computedGaps].sort((a, b) => b.duration - a.duration);

        const now = Date.now();
        const poiTasks: Array<() => Promise<void>> = [];

        const withTimeout = async <T,>(p: Promise<T>, ms: number): Promise<T | null> => {
          let timeoutId: any;
          const timeoutPromise = new Promise<null>((resolve) => {
            timeoutId = setTimeout(() => resolve(null), ms);
          });
          const res = await Promise.race([p, timeoutPromise]);
          clearTimeout(timeoutId);
          return res as any;
        };

        for (const gap of gapsOrdered) {
          const ck = getCacheKeyForGap(gap);
          const cached = gapSuggestionCache.get(ck);
          const gapKey = getGapKey(gap);
          const cacheValid = cached && now - cached.cachedAt < GAP_SUGGESTION_CACHE_TTL_MS;

          if (cacheValid && cached?.poiSuggestions?.length) {
            setGapSuggestions((prev) => ({ ...prev, [gapKey]: cached.poiSuggestions! }));
            setGapLoading((prev) => ({ ...prev, [gapKey]: false }));
            continue;
          }

          // No cache: fetch POIs first (fast + specific), then AI upgrade.
          poiTasks.push(async () => {
            try {
              const poi = await withTimeout(suggestPoisForGap(gap, trip, existingItems, day), 3500);
              if (cancelled) return;
              const normalizedPoi = (poi || []).map((s) => ({ ...s, suggestedStartTime: s.suggestedStartTime || formatHm(gap.start) }));
              if (normalizedPoi.length > 0) {
                gapSuggestionCache.set(ck, { cachedAt: Date.now(), poiSuggestions: normalizedPoi });
                setGapSuggestions((prev) => ({ ...prev, [gapKey]: normalizedPoi }));
              }
            } finally {
        if (!cancelled) {
                setGapLoading((prev) => ({ ...prev, [gapKey]: false }));
              }
            }
          });

        }

        const runWithConcurrency = async (tasks: Array<() => Promise<void>>, limitNum: number) => {
          const queue = [...tasks];
          const workers = new Array(Math.min(limitNum, queue.length)).fill(null).map(async () => {
            while (queue.length && !cancelled) {
              const t = queue.shift();
              if (!t) return;
              await t();
            }
          });
          await Promise.all(workers);
        };

        // POIs first so something specific shows quickly.
        await runWithConcurrency(poiTasks, 2);
      } catch (error) {
        console.error('[FillYourDay] Error loading suggestions:', error);
        if (!cancelled) setSuggestionsError(error instanceof Error ? error.message : 'Failed to analyze this day');
      } finally {
        if (!cancelled) setLoadingSuggestions(false);
      }
    };

    loadSuggestions();

    return () => {
      cancelled = true;
    };
  }, [existingItems, day, trip]);

  const formatHm = (date: Date): string => {
    const h = date.getHours();
    const m = date.getMinutes();
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const getGapKey = (gap: TimeGap) => `${formatHm(gap.start)}-${formatHm(gap.end)}`;

  const getCacheKeyForGap = (gap: TimeGap) => {
    const dayKey = toLocalDayKey(day);
    const tripKey = trip.id || 'no-trip';
    return `${tripKey}:${dayKey}:${getGapKey(gap)}:${gap.duration}`;
  };

  const handleUpgradeGapWithAi = useCallback(
    async (gap: TimeGap, options?: { silent?: boolean }) => {
      const silent = options?.silent === true;
      if (!trip.id) return;
      const gapKey = getGapKey(gap);
      if (aiUpgrading[gapKey]) return;
      if (!isPremium && aiUpgradePromptForGap[gapKey]) {
        openPremiumGate({
          feature: 'ai_itinerary_suggestions',
          title: 'Upgrade for unlimited AI',
          message: 'Free users have limited AI gap suggestions. Upgrade to Premium for unlimited AI planning.',
          source: 'itinerary_fill_day',
        });
        return;
      }

      setAiUpgrading((prev) => ({ ...prev, [gapKey]: true }));
      const startedAt = Date.now();
      void trackAiRequestStarted({
        flow: 'activity_suggestions',
        endpoint: 'suggestGapActivities',
        tier: isPremium ? 'premium' : 'free',
      });
      try {
        const ck = getCacheKeyForGap(gap);
        const cached = gapSuggestionCache.get(ck);
        const now = Date.now();
        const cacheValid = cached && now - cached.cachedAt < GAP_SUGGESTION_CACHE_TTL_MS;

        // If we already have cached AI for this gap, use it instantly—but only because user tapped upgrade.
        if (cacheValid && cached?.aiSuggestions?.length) {
          setGapSuggestions((prev) => ({ ...prev, [gapKey]: cached.aiSuggestions! }));
          setAiShownForGap((prev) => ({ ...prev, [gapKey]: true }));
          return;
        }

        const withTimeout = async <T,>(p: Promise<T>, ms: number): Promise<T | null> => {
          let timeoutId: any;
          const timeoutPromise = new Promise<null>((resolve) => {
            timeoutId = setTimeout(() => resolve(null), ms);
          });
          const res = await Promise.race([p, timeoutPromise]);
          clearTimeout(timeoutId);
          return res as any;
        };

        // Cloud function calls can exceed a few seconds (cold starts/network); avoid false "AI unavailable".
        const ai = await withTimeout(suggestAiActivitiesForGap(gap, trip, existingItems, day), 15000);
        if (!ai || ai.length === 0) {
          void trackAiFallbackUsed({
            flow: 'activity_suggestions',
            endpoint: 'suggestGapActivities',
            reason: 'empty_or_timeout',
          });
          if (!silent) {
            Alert.alert('AI unavailable', 'Could not generate AI suggestions right now. Showing POIs instead.');
          }
          return;
        }

        const normalized = ai.map((s) => ({ ...s, suggestedStartTime: s.suggestedStartTime || formatHm(gap.start) }));
        const prevCached = gapSuggestionCache.get(ck);
        gapSuggestionCache.set(ck, {
          cachedAt: Date.now(),
          poiSuggestions: prevCached?.poiSuggestions,
          aiSuggestions: normalized,
        });
        setGapSuggestions((prev) => ({ ...prev, [gapKey]: normalized }));
        setAiShownForGap((prev) => ({ ...prev, [gapKey]: true }));
        setAiUpgradePromptForGap((prev) => ({ ...prev, [gapKey]: false }));
        void trackAiRequestCompleted({
          flow: 'activity_suggestions',
          endpoint: 'suggestGapActivities',
          tier: isPremium ? 'premium' : 'free',
          latencyMs: Date.now() - startedAt,
          resultCount: normalized.length,
        });
      } catch (error: any) {
        const code = String(error?.code || '').toLowerCase();
        const message = String(error?.message || '').toLowerCase();
        const isRateLimited = code.includes('resource-exhausted') || message.includes('rate limit');
        const feature = String(error?.details?.feature || '');
        if (isRateLimited && !isPremium) {
          setAiUpgradePromptForGap((prev) => ({ ...prev, [gapKey]: true }));
          const limitMessage =
            feature === 'ai_gap_activity_suggestions'
              ? 'You reached your free monthly AI gap suggestion limit. Upgrade to Premium for unlimited AI planning.'
              : 'You reached your free AI suggestion limit. Upgrade to Premium for unlimited AI planning.';
          if (!silent) {
            openPremiumGate({
              feature: 'ai_itinerary_suggestions',
              title: 'Free AI limit reached',
              message: limitMessage,
              source: 'itinerary_fill_day_limit',
            });
          } else if (!autoLimitUpsellShownRef.current) {
            autoLimitUpsellShownRef.current = true;
            openPremiumGate({
              feature: 'ai_itinerary_suggestions',
              title: 'Free AI limit reached',
              message: limitMessage,
              source: 'itinerary_fill_day_auto_limit',
            });
          }
          void trackAiRequestFailed({
            flow: 'activity_suggestions',
            endpoint: 'suggestGapActivities',
            tier: 'free',
            code,
            message,
          });
          return;
        }
        void trackAiRequestFailed({
          flow: 'activity_suggestions',
          endpoint: 'suggestGapActivities',
          tier: isPremium ? 'premium' : 'free',
          code,
          message,
        });
        if (!silent) {
          Alert.alert('AI unavailable', 'Could not generate AI suggestions right now. Please try again.');
        }
      } finally {
        setAiUpgrading((prev) => ({ ...prev, [gapKey]: false }));
      }
    },
    [aiUpgrading, aiUpgradePromptForGap, day, existingItems, isPremium, openPremiumGate, trip],
  );

  useEffect(() => {
    if (!trip.id || loadingSuggestions) return;

    const hasPoiLoadsInFlight = Object.values(gapLoading).some(Boolean);
    if (hasPoiLoadsInFlight) return;

    const eligibleGaps = gaps.filter((gap) => gap.duration >= 60);
    if (eligibleGaps.length === 0) return;

    // Auto-fetch AI picks when user opens "Fill your day".
    // For free users, limit auto-fetch to the largest gap to avoid burning quota across every gap at once.
    const targetGaps = isPremium
      ? eligibleGaps
      : [...eligibleGaps].sort((a, b) => b.duration - a.duration).slice(0, 1);

    targetGaps.forEach((gap) => {
      const gapKey = `${formatHm(gap.start)}-${formatHm(gap.end)}`;
      if (aiShownForGap[gapKey] || aiUpgrading[gapKey] || aiAutoRequestedForGap[gapKey]) return;
      setAiAutoRequestedForGap((prev) => ({ ...prev, [gapKey]: true }));
      void handleUpgradeGapWithAi(gap, { silent: true });
    });
  }, [aiAutoRequestedForGap, aiShownForGap, aiUpgrading, gapLoading, gaps, handleUpgradeGapWithAi, isPremium, loadingSuggestions, trip.id]);

  const parseHm = (t: string): number | null => {
    if (!t || t === 'flexible') return null;
    const [h, m] = t.split(':').map(Number);
    if (!Number.isFinite(h)) return null;
    return h * 60 + (Number.isFinite(m) ? m : 0);
  };

  const overlapsExisting = (startHm: string, endHm?: string) => {
    const s = parseHm(startHm);
    if (s === null) return { overlap: false, count: 0 };
    const e = endHm ? parseHm(endHm) : null;
    const end = e === null ? s + 60 : e; // conservative 60min default
    const overlaps = existingItems.filter((it) => {
      const d = new Date(it.startDate);
      if (d.getFullYear() !== day.getFullYear() || d.getMonth() !== day.getMonth() || d.getDate() !== day.getDate()) {
        return false;
      }
      if (!it.startTime || it.startTime === 'flexible') return false;
      const its = parseHm(it.startTime);
      if (its === null) return false;
      const iteParsed = it.endTime ? parseHm(it.endTime) : null;
      const ite = iteParsed !== null ? iteParsed : its + 60;
      return its < end && s < ite;
    });
    return { overlap: overlaps.length > 0, count: overlaps.length };
  };

  const dayItemsSorted = useMemo(() => {
    const sameDay = existingItems.filter((it) => {
      const d = new Date(it.startDate);
      return d.getFullYear() === day.getFullYear() && d.getMonth() === day.getMonth() && d.getDate() === day.getDate();
    });
    return [...sameDay].sort((a, b) => {
      const aT = a.startTime && a.startTime !== 'flexible' ? parseHm(a.startTime) ?? 99999 : 99999;
      const bT = b.startTime && b.startTime !== 'flexible' ? parseHm(b.startTime) ?? 99999 : 99999;
      return aT - bT;
    });
  }, [existingItems, day]);

  const getGapContext = useCallback(
    (gap: TimeGap) => {
      const s = gap.start.getHours() * 60 + gap.start.getMinutes();
      const e = gap.end.getHours() * 60 + gap.end.getMinutes();
      let prev: ItineraryItem | null = null;
      let next: ItineraryItem | null = null;

      for (const it of dayItemsSorted) {
        const itS = it.startTime && it.startTime !== 'flexible' ? parseHm(it.startTime) : null;
        const itE = it.endTime ? parseHm(it.endTime) : null;
        if (itS !== null && itS <= s) {
          // Prefer an item that truly ends before the gap start
          if (itE !== null && itE <= s) prev = it;
          else if (!prev) prev = it;
        }
        if (!next && itS !== null && itS >= e) {
          next = it;
        }
      }

      return { prev, next };
    },
    [dayItemsSorted],
  );

  const handleAddSuggestionConfirmed = async (suggestion: ActivitySuggestion) => {
    if (!trip.id || addingItem) return;
    const createdBy = firebaseUser?.uid || profile?.id || trip.owner;
    if (!createdBy) {
      Alert.alert('Unable to add item', 'Please sign in again and retry.');
      return;
    }

    setAddingItem(suggestion.title);
    setLoading(true);

    try {
      const [hours, minutes] = suggestion.suggestedStartTime.split(':').map(Number);
      const startDate = new Date(day);
      startDate.setHours(hours, minutes, 0, 0);

      const endDate = suggestion.suggestedEndTime
        ? (() => {
            const [endHours, endMinutes] = suggestion.suggestedEndTime!.split(':').map(Number);
            const end = new Date(day);
            end.setHours(endHours, endMinutes, 0, 0);
            return end;
          })()
        : (() => {
            const end = new Date(startDate);
            end.setMinutes(end.getMinutes() + suggestion.estimatedDuration);
            return end;
          })();

      const itemId = await createItineraryItem(trip.id, {
        title: suggestion.title,
        description: suggestion.description,
        category: suggestion.category,
        startDate,
        endDate,
        startTime: suggestion.suggestedStartTime,
        endTime: suggestion.suggestedEndTime,
        location: suggestion.location,
        source: 'ai',
        status: 'planned',
        createdBy,
        documents: [],
      });
      void trackAiResultAccepted({
        flow: 'activity_suggestions',
        endpoint: 'suggestGapActivities',
        resultType: 'itinerary_item',
        resultId: itemId,
      });

      // Create a partial item object for the callback
      const newItem: Partial<ItineraryItem> = {
        id: itemId,
        tripId: trip.id,
        title: suggestion.title,
        description: suggestion.description,
        category: suggestion.category,
        startDate,
        endDate,
        startTime: suggestion.suggestedStartTime,
        endTime: suggestion.suggestedEndTime,
        location: suggestion.location,
        source: 'ai',
        status: 'planned',
        createdBy,
        documents: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      if (onItemAdded && newItem.id) {
        // The parent component will refresh the items list, so we don't need to pass the full item
        // Just trigger the callback to indicate an item was added
        onItemAdded(newItem as ItineraryItem);
      }

      // Remove the added suggestion
      setGapSuggestions((prev) => {
        const next: Record<string, ActivitySuggestion[]> = {};
        for (const [k, arr] of Object.entries(prev)) {
          next[k] = arr.filter(
            (s) =>
              !(
                s.title === suggestion.title &&
                s.suggestedStartTime === suggestion.suggestedStartTime
              )
          );
        }
        return next;
      });
    } catch (error) {
      console.error('[FillYourDay] Error adding suggestion:', error);
      const msg = error instanceof Error ? error.message : 'Could not add this suggestion right now. Please try again.';
      Alert.alert('Unable to add item', msg);
    } finally {
      setLoading(false);
      setAddingItem(null);
    }
  };

  const handleAddSuggestion = async (suggestion: ActivitySuggestion) => {
    if (!trip.id || addingItem) return;

    const overlapCheck = overlapsExisting(suggestion.suggestedStartTime, suggestion.suggestedEndTime);
    const durationLabel = formatDuration(suggestion.estimatedDuration);
    const overlapLine = overlapCheck.overlap
      ? `\n\n⚠ This overlaps ${overlapCheck.count} existing item${overlapCheck.count === 1 ? '' : 's'}.`
      : '';

    return Alert.alert(
      'Add to itinerary?',
      `${suggestion.title}\n\nTime: ${suggestion.suggestedStartTime} (${durationLabel})${overlapLine}`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Add', style: 'default', onPress: () => handleAddSuggestionConfirmed(suggestion) },
      ]
    );
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (!loadingSuggestions && !suggestionsError && gaps.length === 0) {
    return (
      <Card variant="outlined" style={{ margin: theme.spacing(2) }}>
        <View style={{ alignItems: 'center', padding: theme.spacing(3) }}>
          <Ionicons name="checkmark-circle" size={48} color={theme.colors.success || theme.colors.primary} />
          <SubHeading style={{ marginTop: theme.spacing(1.5) }}>Day is fully planned!</SubHeading>
          <Body color={theme.colors.textSecondary} style={{ textAlign: 'center', marginTop: theme.spacing(0.5) }}>
            Your itinerary for this day is complete. No gaps to fill.
          </Body>
        </View>
      </Card>
    );
  }

  const suggestionsByGap = gapSuggestions;

  const getSuggestionIcon = (category: ActivitySuggestion['category']) => {
    switch (category) {
      case 'restaurant':
        return 'restaurant-outline';
      case 'hotel':
        return 'bed-outline';
      case 'ferry':
        return 'boat-outline';
      case 'transportation':
      case 'train':
      case 'car':
        return 'car-outline';
      default:
        return 'walk-outline';
    }
  };

  return (
    <ScrollView
      style={{ flex: 1 }}
      // Avoid "double padding" because the parent itinerary view already adds horizontal padding.
      contentContainerStyle={{ paddingHorizontal: 0, paddingVertical: theme.spacing(1), gap: theme.spacing(1.5) }}
    >
      {/* Gaps + Suggestions (merged) */}
      {(loadingSuggestions || gaps.length > 0) && (
        <Card
          variant="filled"
          padding={theme.spacing(2)}
          style={{ backgroundColor: theme.colors.primaryContainer || theme.colors.primary + '10' }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5), marginBottom: theme.spacing(2) }}>
            <Ionicons name="sparkles" size={24} color={theme.colors.primary} />
            <SubHeading color={theme.colors.onPrimaryContainer || theme.colors.primary}>
              Suggested activities
            </SubHeading>
            {(loadingSuggestions || Object.values(gapLoading).some(Boolean)) && (
              <Ionicons name="hourglass-outline" size={16} color={theme.colors.primary} style={{ marginLeft: 'auto' }} />
            )}
          </View>
          {loadingSuggestions ? (
            <View style={{ alignItems: 'center', padding: theme.spacing(3) }}>
              <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
                Generating personalized activity suggestions...
              </Body>
            </View>
          ) : (
          <View style={{ gap: theme.spacing(2) }}>
              {gaps.map((gap, idx) => {
                const key = `${formatHm(gap.start)}-${formatHm(gap.end)}`;
                const gapSuggestions = suggestionsByGap[key] || [];
                const { prev, next } = getGapContext(gap);
                const isGapLoading = gapLoading[key];
                const isAiUpgrading = aiUpgrading[key];
                const isAiShown = aiShownForGap[key];
                const hasAiAutoAttempted = !!aiAutoRequestedForGap[key];
                const showUpgradeForFree = !isPremium && !!aiUpgradePromptForGap[key];
                return (
              <Card
                    key={key || idx}
                variant="outlined"
                    padding={theme.spacing(1.5)}
                style={{
                  backgroundColor: theme.colors.surface,
                  borderColor: theme.colors.primary + '30',
                      width: '100%',
                    }}
                  >
                    <View style={{ gap: theme.spacing(1.25) }}>
                      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: theme.spacing(1) }}>
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <ThemedText variant="bodyStrong" numberOfLines={1}>
                            {formatTime(gap.start)} – {formatTime(gap.end)}
                          </ThemedText>
                          <Caption color={theme.colors.textSecondary}>
                            {formatDuration(gap.duration)} available
                            {next?.title ? ` • Next: ${next.title}` : ''}
                          </Caption>
                          {!!(prev?.location || prev?.address || next?.location || next?.address) && (
                            <Caption color={theme.colors.textSecondary} numberOfLines={1}>
                              Near {String((prev?.location || prev?.address || next?.location || next?.address) ?? '').trim()}
                            </Caption>
                          )}
                        </View>
                        <Ionicons name="arrow-forward" size={18} color={theme.colors.textSecondary} />
                      </View>

                      {isGapLoading ? (
                        <View style={{ paddingVertical: theme.spacing(0.5) }}>
                          <Caption color={theme.colors.textSecondary}>Finding ideas for this gap…</Caption>
                        </View>
                      ) : null}

                      {trip.id && !isAiShown ? (
                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                          {isAiUpgrading ? (
                            <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                              Generating AI picks...
                            </Caption>
                          ) : hasAiAutoAttempted ? (
                            <Pressable
                              onPress={() => handleUpgradeGapWithAi(gap)}
                              accessibilityRole="button"
                              accessibilityLabel={showUpgradeForFree ? 'Upgrade for unlimited AI' : 'Retry AI picks'}
                              style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
                            >
                              <Caption color={theme.colors.primary} style={{ fontSize: 12, fontWeight: '700' }}>
                                {showUpgradeForFree ? 'Upgrade for unlimited AI' : 'Retry AI picks'}
                              </Caption>
                            </Pressable>
                          ) : (
                            <Button
                              label={
                                showUpgradeForFree
                                  ? 'Upgrade for unlimited AI'
                                  : 'Generate AI picks'
                              }
                              size="small"
                              variant="secondary"
                              onPress={() => handleUpgradeGapWithAi(gap)}
                            />
                          )}
                        </View>
                      ) : isAiShown ? (
                        <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                          AI picks
                        </Caption>
                      ) : null}

                      {gapSuggestions.length === 0 ? (
                        <View style={{ gap: theme.spacing(1) }}>
                          <Body color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                            No suggestions for this time gap yet.
                          </Body>
                          <Button label="Try again" size="small" variant="secondary" onPress={refreshSuggestions} />
                        </View>
                      ) : (
                        <View style={{ gap: theme.spacing(1.5) }}>
                          {gapSuggestions.map((suggestion, index) => (
                            <Card
                              key={`${key}-${index}`}
                              variant="outlined"
                              padding={theme.spacing(1.5)}
                              style={{
                                backgroundColor: theme.colors.surface,
                                borderColor: theme.colors.borderMuted,
                }}
              >
                <View style={{ gap: theme.spacing(1) }}>
                                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(1) }}>
                                  <View style={{ flex: 1, minWidth: 0 }}>
                                    <ThemedText variant="bodyStrong" numberOfLines={2}>
                                      {suggestion.title}
                                    </ThemedText>
                      {suggestion.description && (
                                      <Body
                                        color={theme.colors.textSecondary}
                                        style={{ marginTop: theme.spacing(0.25), fontSize: 13 }}
                                        numberOfLines={3}
                                      >
                          {suggestion.description}
                        </Body>
                      )}
                    </View>
                                  <Ionicons name={getSuggestionIcon(suggestion.category) as any} size={20} color={theme.colors.primary} />
                  </View>

                                {/* Metadata row (stacked to avoid overflow) */}
                                <View style={{ gap: theme.spacing(0.5), marginTop: theme.spacing(0.25) }}>
                                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                      <Ionicons name="time-outline" size={14} color={theme.colors.textSecondary} />
                      <Caption color={theme.colors.textSecondary}>
                        {suggestion.suggestedStartTime} • {formatDuration(suggestion.estimatedDuration)}
                      </Caption>
                                    </View>
                                    {overlapsExisting(suggestion.suggestedStartTime, suggestion.suggestedEndTime).overlap && (
                                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                                        <Ionicons name="warning-outline" size={14} color={theme.colors.warning} />
                                        <Caption color={theme.colors.warning} style={{ fontSize: 12 }}>
                                          Overlaps
                                        </Caption>
                                      </View>
                                    )}
                    </View>
                    {suggestion.location && (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                        <Ionicons name="location-outline" size={14} color={theme.colors.textSecondary} />
                                      <Caption color={theme.colors.textSecondary} numberOfLines={1} style={{ flex: 1 }}>
                          {suggestion.location}
                        </Caption>
                      </View>
                    )}
                  </View>

                                {!!suggestion.reason && (
                                  <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(0.5), marginTop: theme.spacing(0.25) }}>
                                    <Ionicons name="information-circle-outline" size={12} color={theme.colors.textSecondary} style={{ marginTop: 2 }} />
                                    <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, flex: 1 }} numberOfLines={2}>
                                      {suggestion.reason}
                                    </Caption>
                                  </View>
                                )}

                                {/* Voting + Action */}
                  <View
                    style={{
                    flexDirection: 'row', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    paddingTop: theme.spacing(1),
                    marginTop: theme.spacing(1),
                    borderTopWidth: 1,
                    borderTopColor: theme.colors.borderMuted,
                                  }}
                                >
                    <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                      Group vote
                    </Caption>
                    <VoteButton
                      tripId={trip.id}
                      itemId={`fill-day-${suggestion.title}-${suggestion.suggestedStartTime}`}
                      itemType="activity"
                      size="small"
                      showCounts={true}
                      orientation="horizontal"
                    />
                  </View>

                  <Button
                                  label={addingItem === suggestion.title ? 'Adding...' : 'Add to itinerary'}
                    variant="secondary"
                    onPress={() => handleAddSuggestion(suggestion)}
                    disabled={loading || addingItem !== null}
                    loading={addingItem === suggestion.title}
                    style={{ marginTop: theme.spacing(1) }}
                    icon={
                      addingItem !== suggestion.title ? (
                        <Ionicons name="add-circle-outline" size={18} color={theme.colors.primary} />
                      ) : undefined
                    }
                  />
                </View>
              </Card>
            ))}
                        </View>
                      )}
                    </View>
                  </Card>
                );
              })}
          </View>
          )}
        </Card>
      )}

      {/* Empty State */}
      {gaps.length === 0 && !loadingSuggestions && (
        <Card variant="outlined">
          <View style={{ alignItems: 'center', padding: theme.spacing(3) }}>
            <Ionicons name="checkmark-circle-outline" size={40} color={theme.colors.textSecondary} />
            <Body color={theme.colors.textSecondary} style={{ textAlign: 'center', marginTop: theme.spacing(1) }}>
              No time gaps found for this day.
            </Body>
          </View>
        </Card>
      )}
    </ScrollView>
  );
}

